import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Truck, RotateCcw, Shield, Star, Minus, Plus, ChevronRight } from 'lucide-react';
import Header from './components/Header5';
import Footer from './components/Footer5';
import Button from './components/Button';
import ProductCard from './components/ProductCard';

/**
 * Template 5: TARA — Product Detail Page
 * Editorial layout with large imagery and refined typography
 */
const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedSize, setSelectedSize] = useState('M');
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);
    const [isWishlisted, setIsWishlisted] = useState(false);

    const product = {
        id: id,
        name: 'Chanderi Silk Saree',
        price: 12999,
        originalPrice: 15999,
        rating: 4.8,
        reviews: 86,
        description: 'A masterfully crafted Chanderi silk saree featuring intricate zari work and a rich pallu. This piece embodies the timeless elegance of Indian handloom tradition, reimagined for the modern woman.',
        features: [
            'Pure Chanderi silk with gold zari border',
            'Handwoven by master artisans',
            'Includes matching blouse piece',
            'Dry clean recommended',
            'Length: 6.3 meters with blouse',
        ],
        images: [
            'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=800',
        ],
        sizes: ['XS', 'S', 'M', 'L', 'XL'],
        inStock: true,
    };

    const relatedProducts = [
        { id: 2, name: 'Embroidered Lehenga', category: 'Lehengas', price: 34999, image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Banarasi Silk Kurta', category: 'Kurtas', price: 8499, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Kanjivaram Drape', category: 'Sarees', price: 24999, image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Organza Cape Set', category: 'Indo-Western', price: 14999, image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    return (
        <div className="w-full min-h-screen" style={{ backgroundColor: '#FAF9F6', fontFamily: "'Outfit', sans-serif" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Outfit:wght@300;400;500;600&display=swap');
            `}</style>
            <Header />

            <main className="w-full">
                {/* Breadcrumb */}
                <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-4">
                    <div className="flex items-center gap-2 text-xs text-[#71717A]" style={{ fontWeight: 300 }}>
                        <span className="cursor-pointer hover:text-[#1A1A1A] transition-colors" onClick={() => navigate('/template5')}>Home</span>
                        <ChevronRight className="w-3 h-3" />
                        <span className="cursor-pointer hover:text-[#1A1A1A] transition-colors" onClick={() => navigate('/template5/shop')}>Shop</span>
                        <ChevronRight className="w-3 h-3" />
                        <span className="text-[#1A1A1A]">{product.name}</span>
                    </div>
                </div>

                {/* Product Section */}
                <section className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 pb-16 md:pb-24">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
                        {/* Image Gallery */}
                        <div>
                            {/* Main Image */}
                            <div className="relative overflow-hidden bg-[#F5F4F0] aspect-[3/4] mb-3 md:mb-4">
                                <img
                                    src={product.images[selectedImage]}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            {/* Thumbnails */}
                            <div className="grid grid-cols-4 gap-2 md:gap-3">
                                {product.images.map((img, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedImage(index)}
                                        className={`relative overflow-hidden aspect-square transition-all duration-300 ${selectedImage === index ? 'ring-1 ring-[#2C2C2C]' : 'opacity-60 hover:opacity-100'}`}
                                    >
                                        <img src={img} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Product Info */}
                        <div className="py-2 lg:py-6">
                            <p
                                className="text-[11px] md:text-xs tracking-[0.2em] uppercase text-[#71717A] mb-2"
                                style={{ fontWeight: 300 }}
                            >
                                Sarees
                            </p>
                            <h1
                                className="text-2xl md:text-3xl lg:text-4xl text-[#1A1A1A] mb-3"
                                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                            >
                                {product.name}
                            </h1>

                            {/* Rating */}
                            <div className="flex items-center gap-3 mb-4">
                                <div className="flex items-center gap-1">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} size={13} className={i < Math.floor(product.rating) ? 'text-[#2C2C2C] fill-[#2C2C2C]' : 'text-[#E4E4E7]'} />
                                    ))}
                                </div>
                                <span className="text-xs text-[#71717A]" style={{ fontWeight: 300 }}>{product.rating} ({product.reviews} reviews)</span>
                            </div>

                            {/* Price */}
                            <div className="flex items-center gap-3 mb-6">
                                <span className="text-xl md:text-2xl text-[#1A1A1A]" style={{ fontWeight: 500 }}>
                                    ₹{product.price.toLocaleString('en-IN')}
                                </span>
                                {product.originalPrice && (
                                    <span className="text-base text-[#71717A] line-through" style={{ fontWeight: 300 }}>
                                        ₹{product.originalPrice.toLocaleString('en-IN')}
                                    </span>
                                )}
                            </div>

                            {/* Description */}
                            <p className="text-sm text-[#71717A] leading-relaxed mb-6 font-light">
                                {product.description}
                            </p>

                            {/* Size Selection */}
                            <div className="mb-6">
                                <p className="text-xs tracking-[0.1em] uppercase text-[#1A1A1A] mb-3" style={{ fontWeight: 500 }}>
                                    Size
                                </p>
                                <div className="flex gap-2">
                                    {product.sizes.map((size) => (
                                        <button
                                            key={size}
                                            onClick={() => setSelectedSize(size)}
                                            className={`w-10 h-10 md:w-11 md:h-11 text-xs tracking-wide transition-all duration-300 ${selectedSize === size ? 'bg-[#2C2C2C] text-white' : 'border border-[#E4E4E7] text-[#1A1A1A] hover:border-[#2C2C2C]'}`}
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Quantity */}
                            <div className="mb-8">
                                <p className="text-xs tracking-[0.1em] uppercase text-[#1A1A1A] mb-3" style={{ fontWeight: 500 }}>
                                    Quantity
                                </p>
                                <div className="inline-flex items-center border border-[#E4E4E7]">
                                    <button
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        className="w-10 h-10 flex items-center justify-center text-[#71717A] hover:text-[#1A1A1A] transition-colors"
                                    >
                                        <Minus size={14} />
                                    </button>
                                    <span className="w-12 h-10 flex items-center justify-center text-sm text-[#1A1A1A] border-x border-[#E4E4E7]">
                                        {quantity}
                                    </span>
                                    <button
                                        onClick={() => setQuantity(quantity + 1)}
                                        className="w-10 h-10 flex items-center justify-center text-[#71717A] hover:text-[#1A1A1A] transition-colors"
                                    >
                                        <Plus size={14} />
                                    </button>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 mb-8">
                                <Button variant="primary" size="lg" className="flex-1" onClick={() => navigate('/template5/cart')}>
                                    <ShoppingCart size={16} /> Add to Cart
                                </Button>
                                <button
                                    onClick={() => setIsWishlisted(!isWishlisted)}
                                    className={`w-12 h-12 md:w-14 md:h-14 border flex items-center justify-center transition-all duration-300 ${isWishlisted ? 'border-[#2C2C2C] bg-[#2C2C2C]' : 'border-[#E4E4E7] hover:border-[#2C2C2C]'}`}
                                >
                                    <Heart size={18} className={isWishlisted ? 'text-white fill-white' : 'text-[#2C2C2C]'} />
                                </button>
                            </div>

                            {/* Trust Badges */}
                            <div className="grid grid-cols-3 gap-4 py-6 border-t border-[#E4E4E7]">
                                <div className="text-center">
                                    <Truck size={18} className="mx-auto mb-2 text-[#2C2C2C]" />
                                    <p className="text-[10px] text-[#71717A] tracking-wide uppercase" style={{ fontWeight: 300 }}>Free Shipping</p>
                                </div>
                                <div className="text-center">
                                    <RotateCcw size={18} className="mx-auto mb-2 text-[#2C2C2C]" />
                                    <p className="text-[10px] text-[#71717A] tracking-wide uppercase" style={{ fontWeight: 300 }}>Easy Returns</p>
                                </div>
                                <div className="text-center">
                                    <Shield size={18} className="mx-auto mb-2 text-[#2C2C2C]" />
                                    <p className="text-[10px] text-[#71717A] tracking-wide uppercase" style={{ fontWeight: 300 }}>Authenticity</p>
                                </div>
                            </div>

                            {/* Features */}
                            <div className="pt-6 border-t border-[#E4E4E7]">
                                <p className="text-xs tracking-[0.1em] uppercase text-[#1A1A1A] mb-3" style={{ fontWeight: 500 }}>Details</p>
                                <ul className="space-y-2">
                                    {product.features.map((feature, index) => (
                                        <li key={index} className="flex items-start gap-2 text-sm text-[#71717A] font-light">
                                            <span className="w-1 h-1 rounded-full bg-[#2C2C2C] mt-2 flex-shrink-0" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Related Products */}
                <section className="py-14 md:py-20 border-t border-[#E4E4E7]" style={{ backgroundColor: '#F5F4F0' }}>
                    <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
                        <h2
                            className="text-2xl md:text-3xl text-[#1A1A1A] mb-8 md:mb-10 text-center"
                            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                        >
                            You May Also Love
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                            {relatedProducts.map((product) => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default ProductDetail;
